import { CloseRounded, FullscreenRounded, PauseRounded, PlayArrowRounded, ReplayRounded } from '@mui/icons-material'
import { Box, Button, Dialog, DialogContent, IconButton, Slider, Stack, Typography } from '@mui/material'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAccountsStore } from '../../store/AccountsStore'
import { StripeConnectButton } from '../Adopters/StripeConnectButton'

type Props = {
  open: boolean
  onClose: () => void
}

/**
 * Starting points, replaced by each video's real duration once its metadata loads. Hardcoding them
 * alone would silently desynchronise the progress bar the day a video is re-exported.
 */
const fallbackVideoDurations = [34.77, 20, 6.97]
const videoSources = [
  '/payment-tutorial/01_Association_Creation_Demande.mp4?v=2',
  '/payment-tutorial/02_Adoptant_Email_Paiement.mp4?v=2',
  '/payment-tutorial/03_Association_Confirmation_Paiement.mp4?v=2',
]

const paymentBenefits = [
  [
    '↗',
    '1. Envoyez une demande',
    'En quelques clics depuis votre espace association lors d’une nouvelle adoption via « Recevoir un paiement »',
  ],
  ['▣', '2. L’adoptant paie', 'Par un lien reçu par email, sans frais pour l’association'],
  ['✓', '3. Recevez le règlement', 'Virement sous 7 jours ou manuel via Stripe · Suivi dans la procédure d’adoption'],
]

const PaymentBenefits: React.FC<{ activeVideoIndex: number; isVideoOnly: boolean }> = ({
  activeVideoIndex,
  isVideoOnly,
}) => (
  <Box
    sx={{
      display: isVideoOnly ? 'none' : 'grid',
      gridTemplateColumns: {
        xs: activeVideoIndex === 0 ? '3fr 1fr 1fr' : activeVideoIndex === 1 ? '1fr 3fr 1fr' : '1fr 1fr 3fr',
        md: 'repeat(3, minmax(0, 1fr))',
      },
      gap: 1,
      mt: 1.25,
      transition: 'grid-template-columns .45s ease',
    }}
  >
    {paymentBenefits.map(([icon, title, description], cardIndex) => {
      const isCurrentStep = cardIndex === activeVideoIndex
      const isFutureStep = cardIndex > activeVideoIndex

      return (
        <Box
          key={title}
          sx={{
            bgcolor: isCurrentStep ? '#FFF9EC' : '#F5F8FE',
            border: isCurrentStep ? '1px solid #FCB402' : '1px solid #E1E9F8',
            borderRadius: 3,
            boxShadow: isCurrentStep ? '0 8px 20px rgba(252, 180, 2, 0.18)' : 'none',
            display: 'flex',
            filter: {
              xs: isCurrentStep ? 'none' : 'blur(1.2px) saturate(.7)',
              md: isFutureStep ? 'blur(1.5px) saturate(.7)' : 'none',
            },
            gap: 1,
            justifyContent: { xs: isCurrentStep ? 'flex-start' : 'center', md: 'flex-start' },
            minWidth: 0,
            opacity: { xs: isCurrentStep ? 1 : 0.42, md: isFutureStep ? 0.48 : 1 },
            p: { xs: 0.75, md: 1 },
            transform: isCurrentStep ? 'translateY(-3px) scale(1.02)' : isFutureStep ? 'scale(.94)' : 'none',
            transition: 'all .45s ease',
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: isCurrentStep ? '#FFE9AD' : '#E2EBFF',
              borderRadius: 2,
              color: isCurrentStep ? '#9C6200' : '#305B9E',
              display: 'flex',
              flex: '0 0 auto',
              fontSize: 16,
              height: 28,
              justifyContent: 'center',
              width: 28,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ display: { xs: isCurrentStep ? 'block' : 'none', md: 'block' }, minWidth: 0 }}>
            <Typography sx={{ color: '#203B64', fontSize: { xs: 10, md: 11.5 }, fontWeight: 700, lineHeight: 1.25 }}>
              {title}
            </Typography>
            <Typography sx={{ color: '#77849D', fontSize: { xs: 9, md: 10 }, lineHeight: 1.35, mt: 0.35 }}>
              {cardIndex === 0 ? (
                <>
                  En quelques clics depuis votre espace association lors d&apos;une nouvelle adoption via{' '}
                  <Box component="span" sx={{ fontWeight: 800 }}>
                    « Recevoir un paiement »
                  </Box>
                </>
              ) : cardIndex === 1 ? (
                <>
                  Par un lien reçu par email,{' '}
                  <Box component="span" sx={{ fontWeight: 800 }}>
                    sans frais pour l&apos;association
                  </Box>
                </>
              ) : cardIndex === 2 ? (
                <>
                  <Box component="span" sx={{ fontWeight: 800 }}>
                    Virement directement sur votre compte bancaire
                  </Box>{' '}
                  · Suivi dans la procédure d&apos;adoption
                </>
              ) : (
                description
              )}
            </Typography>
          </Box>
        </Box>
      )
    })}
  </Box>
)

export const PaymentsTutorialModal: React.FC<Props> = ({ open, onClose }) => {
  const accountsStore = useAccountsStore()
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([])
  const [activeVideoIndex, setActiveVideoIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [elapsedDuration, setElapsedDuration] = useState(0)
  const [isVideoOnly, setIsVideoOnly] = useState(false)

  const [videoDurations, setVideoDurations] = useState(fallbackVideoDurations)
  const totalDuration = useMemo(() => videoDurations.reduce((total, duration) => total + duration, 0), [videoDurations])

  const handleDurationKnown = (videoIndex: number, duration: number) => {
    if (!Number.isFinite(duration) || duration <= 0) {
      return
    }

    setVideoDurations((durations) =>
      durations[videoIndex] === duration
        ? durations
        : durations.map((current, index) => (index === videoIndex ? duration : current))
    )
  }

  const isAdmin = accountsStore.isAdmin()
  const activeDesktopVideoIndex = activeVideoIndex === 2 ? 2 : 0

  const stopVideos = () => {
    videoRefs.current.forEach((video) => video?.pause())
    setIsPlaying(false)
  }

  const resetVideos = () => {
    stopVideos()
    videoRefs.current.forEach((video) => {
      if (video) {
        video.currentTime = 0
      }
    })
    setActiveVideoIndex(0)
    setElapsedDuration(0)
  }

  useEffect(() => {
    if (open) {
      resetVideos()
      setIsPlaying(true)
      return
    }

    stopVideos()
    setIsVideoOnly(false)
  }, [open])

  useEffect(() => {
    // Read when the effect runs, never while rendering. The dialog mounts its content only as it
    // opens, so during that render the refs are still empty; an element captured there is null and
    // the first clip is never asked to play. By the time effects run the refs are attached.
    const video = videoRefs.current[activeVideoIndex]
    if (!open || !isPlaying || !video) {
      return
    }

    video.play().catch(() => setIsPlaying(false))
  }, [open, activeVideoIndex, isPlaying])

  const handleClose = () => {
    stopVideos()
    setIsVideoOnly(false)
    onClose()
  }

  const handlePlayPause = () => {
    const activeVideo = videoRefs.current[activeVideoIndex]
    if (!activeVideo) {
      return
    }

    if (activeVideo.ended) {
      return
    }

    if (isPlaying) {
      activeVideo.pause()
      setIsPlaying(false)
      return
    }

    setIsPlaying(true)
  }

  const handleVideoEnded = () => {
    if (activeVideoIndex === videoSources.length - 1) {
      setIsPlaying(false)
      return
    }

    setActiveVideoIndex((currentIndex) => currentIndex + 1)
  }

  const handleProgress = (_event: Event, value: number | number[]) => {
    const targetDuration = Array.isArray(value) ? value[0] : value
    let remainingDuration = targetDuration
    let targetVideoIndex = 0

    for (let index = 0; index < videoDurations.length; index += 1) {
      if (remainingDuration <= videoDurations[index] || index === videoDurations.length - 1) {
        targetVideoIndex = index
        break
      }
      remainingDuration -= videoDurations[index]
    }

    stopVideos()
    const targetVideo = videoRefs.current[targetVideoIndex]
    if (targetVideo) {
      targetVideo.currentTime = Math.min(remainingDuration, videoDurations[targetVideoIndex])
    }
    setActiveVideoIndex(targetVideoIndex)
    setElapsedDuration(targetDuration)
  }

  /**
   * Only the first clip is fetched when the dialog opens; most people close it straight away and
   * the other two would be pure waste. The next one is warmed as soon as the current starts, so the
   * change of clip stays seamless.
   */
  useEffect(() => {
    if (!open) {
      return
    }

    const nextVideo = videoRefs.current[activeVideoIndex + 1]
    if (nextVideo && nextVideo.preload !== 'auto') {
      nextVideo.preload = 'auto'
      nextVideo.load()
    }
  }, [open, activeVideoIndex])

  const activeVideoOffset = useMemo(
    () => videoDurations.slice(0, activeVideoIndex).reduce((total, duration) => total + duration, 0),
    [activeVideoIndex]
  )

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      BackdropProps={{ sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(21, 34, 58, 0.35)' } }}
      PaperProps={{
        sx: {
          height: isVideoOnly ? 'calc(100dvh - 24px)' : 'auto',
          maxHeight: 'calc(100dvh - 24px)',
          maxWidth: isVideoOnly ? 1400 : 970,
          borderRadius: 5,
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        sx={{
          display: isVideoOnly ? 'flex' : 'block',
          flexDirection: 'column',
          overflowY: 'auto',
          p: isVideoOnly ? 1 : { xs: 1.5, md: 3 },
          position: 'relative',
        }}
      >
        {isVideoOnly && (
          <IconButton
            aria-label="Réduire la vidéo"
            onClick={() => setIsVideoOnly(false)}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.92)',
              color: '#78859A',
              position: 'absolute',
              right: 16,
              top: 16,
              zIndex: 2,
            }}
          >
            <CloseRounded />
          </IconButton>
        )}
        <Box sx={{ display: isVideoOnly ? 'none' : 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            sx={{
              border: '1px solid #F5CE83',
              borderRadius: 99,
              bgcolor: '#FFF3D8',
              color: '#9C6200',
              px: 1.25,
              py: 0.5,
              fontSize: 12,
              fontWeight: 750,
            }}
          >
            Comment marche Petso ?
          </Typography>
          <IconButton
            aria-label="Fermer la démonstration"
            onClick={handleClose}
            size="small"
            sx={{ bgcolor: '#F2F5F9', color: '#78859A' }}
          >
            <CloseRounded />
          </IconButton>
        </Box>

        <Typography
          align="center"
          sx={{
            color: '#173766',
            display: isVideoOnly ? 'none' : 'block',
            fontSize: { xs: 22, md: 26 },
            fontWeight: 800,
            letterSpacing: '-0.75px',
            lineHeight: 1.18,
            mt: 1.25,
          }}
        >
          Encaissez vos frais d&apos;adoption en ligne, gratuitement !
        </Typography>
        <Typography
          align="center"
          sx={{
            color: '#687791',
            display: isVideoOnly ? 'none' : 'block',
            fontSize: { xs: 12, md: 13 },
            mt: 0.75,
            mb: 1.5,
          }}
        >
          Suivi automatique centralisé sur votre espace Petso.
        </Typography>

        <Box
          sx={{
            alignItems: 'center',
            background: 'linear-gradient(110deg, #FFF7E4 0%, #FFFDF8 100%)',
            border: '1px solid #F5D99A',
            borderRadius: 3,
            display: isVideoOnly ? 'none' : 'flex',
            gap: 1.25,
            mb: 1.75,
            p: { xs: 1, md: 1.25 },
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: '#FFE9AD',
              borderRadius: '50%',
              display: 'flex',
              flex: '0 0 auto',
              fontSize: 20,
              height: 38,
              justifyContent: 'center',
              width: 38,
            }}
          >
            💛
          </Box>
          <Box>
            <Typography sx={{ color: '#716647', fontSize: { xs: 11, md: 12 }, lineHeight: 1.45 }}>
              <Box component="span" sx={{ color: '#9C6200', fontSize: 10, fontWeight: 800, letterSpacing: 0.8, mr: 1 }}>
                IMPORTANT POUR PETSO
              </Box>
              Les pourboires facultatifs, proposés aux adoptants lors du paiement, sont essentiels pour faire vivre
              Petso et rendre la plateforme accessible aux associations.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: isVideoOnly ? 'none' : 'block', mb: 0.75 }}>
          <Typography align="center" sx={{ color: '#173766', fontSize: { xs: 14, md: 15 }, fontWeight: 800 }}>
            Et surtout, très pratique pour vous.
          </Typography>
          <Typography align="center" sx={{ color: '#7A8197', fontSize: 12, fontWeight: 500, mt: 0.2 }}>
            Découvrez le parcours en 1 minute
          </Typography>
        </Box>

        <Box
          sx={{
            alignItems: 'center',
            display: 'grid',
            gap: 1,
            gridTemplateColumns: 'minmax(0, 16fr) minmax(0, 4.15fr)',
            aspectRatio: isVideoOnly ? '2.24' : undefined,
            height: isVideoOnly ? 'auto' : { xs: 170, sm: 230, md: 300 },
            justifyContent: 'center',
            mx: 'auto',
            mt: isVideoOnly ? 'auto' : 0,
            overflow: isVideoOnly ? 'visible' : 'hidden',
            width: isVideoOnly ? '100%' : { xs: 'min(100%, 381px)', sm: 'min(100%, 515px)', md: 'min(100%, 672px)' },
          }}
        >
          <Box
            sx={{
              height: '100%',
              flexShrink: 0,
              minWidth: 0,
              position: 'relative',
              width: '100%',
            }}
          >
            <Typography
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.92)',
                borderRadius: 99,
                color: '#1C3C73',
                fontSize: { xs: 9, md: 11 },
                fontWeight: 700,
                left: 8,
                px: 1,
                py: 0.5,
                position: 'absolute',
                boxShadow: '0 2px 8px rgba(28, 60, 115, 0.22)',
                top: 8,
                zIndex: 1,
              }}
            >
              🖥️ Votre espace association
            </Typography>
            <Box
              sx={{
                filter: activeVideoIndex === 1 ? 'blur(4px) saturate(.6)' : 'none',
                height: '100%',
                opacity: activeVideoIndex === 1 ? 0.58 : 1,
                transition: 'filter .4s, opacity .4s',
              }}
            >
              {[0, 2].map((videoIndex) => (
                <Box
                  key={videoIndex}
                  component="video"
                  muted
                  playsInline
                  preload={videoIndex === 0 ? 'auto' : 'metadata'}
                  ref={(video: HTMLVideoElement | null) => {
                    videoRefs.current[videoIndex] = video
                  }}
                  src={videoSources[videoIndex]}
                  onLoadedMetadata={(event: React.SyntheticEvent<HTMLVideoElement>) =>
                    handleDurationKnown(videoIndex, event.currentTarget.duration)
                  }
                  onEnded={videoIndex === activeVideoIndex ? handleVideoEnded : undefined}
                  onTimeUpdate={(event: React.SyntheticEvent<HTMLVideoElement>) => {
                    if (videoIndex === activeVideoIndex)
                      setElapsedDuration(activeVideoOffset + event.currentTarget.currentTime)
                  }}
                  sx={{
                    display: activeDesktopVideoIndex === videoIndex ? 'block' : 'none',
                    height: '100%',
                    width: '100%',
                    objectFit: 'contain',
                  }}
                />
              ))}
            </Box>
          </Box>
          <Box
            sx={{
              aspectRatio: '384 / 832',
              borderRadius: 2,
              height: '100%',
              flexShrink: 0,
              minWidth: 0,
              overflow: 'hidden',
              position: 'relative',
              width: '100%',
            }}
          >
            <Typography
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.92)',
                borderRadius: 99,
                color: '#1C3C73',
                fontSize: { xs: 8, md: 10 },
                fontWeight: 700,
                left: 4,
                px: 0.75,
                py: 0.4,
                position: 'absolute',
                boxShadow: '0 2px 8px rgba(28, 60, 115, 0.22)',
                top: 4,
                zIndex: 1,
              }}
            >
              <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                📱 Adoptant
              </Box>
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                📱 Côté adoptant
              </Box>
            </Typography>
            <Box
              sx={{
                filter: activeVideoIndex === 1 ? 'none' : 'blur(4px) saturate(.6)',
                height: '100%',
                opacity: activeVideoIndex === 1 ? 1 : 0.58,
                transition: 'filter .4s, opacity .4s',
              }}
            >
              <Box
                component="video"
                muted
                playsInline
                preload="metadata"
                ref={(video: HTMLVideoElement | null) => {
                  videoRefs.current[1] = video
                }}
                src={videoSources[1]}
                onLoadedMetadata={(event: React.SyntheticEvent<HTMLVideoElement>) =>
                  handleDurationKnown(1, event.currentTarget.duration)
                }
                onEnded={activeVideoIndex === 1 ? handleVideoEnded : undefined}
                onTimeUpdate={(event: React.SyntheticEvent<HTMLVideoElement>) => {
                  if (activeVideoIndex === 1) setElapsedDuration(activeVideoOffset + event.currentTarget.currentTime)
                }}
                sx={{ height: '100%', width: '100%', objectFit: 'contain' }}
              />
            </Box>
          </Box>
        </Box>

        <PaymentBenefits activeVideoIndex={activeVideoIndex} isVideoOnly={isVideoOnly} />

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: isVideoOnly ? 'auto' : 0, mt: 0.75 }}>
          <IconButton
            aria-label={isPlaying ? 'Mettre en pause' : 'Lire la démonstration'}
            onClick={handlePlayPause}
            size="small"
            sx={{ bgcolor: '#FFAF00', color: 'white', '&:hover': { bgcolor: '#E69E00' } }}
          >
            {isPlaying ? <PauseRounded fontSize="small" /> : <PlayArrowRounded fontSize="small" />}
          </IconButton>
          <Slider
            aria-label="Progression de la démonstration"
            min={0}
            max={totalDuration}
            step={0.1}
            value={elapsedDuration}
            onChange={handleProgress}
            sx={{ color: '#F8AE00' }}
          />
          <IconButton
            aria-label="Recommencer la démonstration"
            onClick={resetVideos}
            size="small"
            sx={{ color: '#74829B' }}
          >
            <ReplayRounded fontSize="small" />
          </IconButton>
          {!isVideoOnly && (
            <IconButton
              aria-label="Agrandir la vidéo"
              onClick={() => setIsVideoOnly(true)}
              size="small"
              sx={{
                '@keyframes paymentTutorialFullscreenHint': {
                  '0%, 100%': { transform: 'translateY(0) scale(1)' },
                  '40%': { transform: 'translateY(-7px) scale(1.12)' },
                  '65%': { transform: 'translateY(-2px) scale(1.04)' },
                },
                animation: 'paymentTutorialFullscreenHint 1.45s ease-in-out infinite',
                bgcolor: '#E8F1FF',
                color: '#1976D2',
                '&:hover': { bgcolor: '#D8E8FF' },
              }}
            >
              <FullscreenRounded fontSize="small" />
            </IconButton>
          )}
        </Stack>

        <Box sx={{ display: isVideoOnly ? 'none' : 'flex', justifyContent: 'center', mt: 1.75 }}>
          {isAdmin ? (
            <StripeConnectButton
              color="primary"
              content={
                <>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    Configurer et demander des frais d&apos;adoption
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Configurer et réaliser une demande de frais d&apos;adoption
                  </Box>
                </>
              }
              fullWidth={false}
              onStripeConnectOpened={onClose}
              sx={{ minWidth: { xs: '100%', sm: 570 }, py: 1.25, textTransform: 'none' }}
            />
          ) : (
            <Button
              disabled
              variant="contained"
              sx={{ minWidth: { xs: '100%', sm: 570 }, py: 1.25, textTransform: 'none' }}
            >
              Demander à votre administrateur de configurer la fonctionnalité
            </Button>
          )}
        </Box>
        <Box
          sx={{
            alignItems: 'center',
            color: '#6B7890',
            display: isVideoOnly ? 'none' : 'flex',
            gap: 1,
            justifyContent: 'center',
            mt: 1,
          }}
        >
          <Box component="span" sx={{ fontSize: 23, lineHeight: 1 }}>
            💡
          </Box>
          <Typography align="center" sx={{ fontSize: { xs: 12, md: 13 }, lineHeight: 1.5, maxWidth: 760 }}>
            Afin de percevoir vos futurs paiements et d&apos;accéder à votre tableau de bord de suivi des encaissements,
            une configuration d&apos;environ <strong>3 minutes</strong> via Stripe Connect est nécessaire.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
